import { useDisclosure } from '@chakra-ui/react'
import {
	DndContext,
	DragEndEvent,
	DragOverlay,
	DragStartEvent
} from '@dnd-kit/core'
import cn from 'clsx'
import { FC, useState } from 'react'

import DialogWindow from '@/components/DialogWindow'
import { Typography } from '@/components/ui'
import CustomInput from '@/components/ui/input/CustomInput'
import Spinner from '@/components/ui/spinner/Spinner'
import CustomTextarea from '@/components/ui/textarea/CustomTextArea'

import Cancel from '@/assets/icons/cancel-3.svg'
import Check from '@/assets/icons/check.svg'

import {
	IDeleteHighlightParams,
	IHighlightsFolder,
	IMoveSegmentParams,
	ISegment,
	IUpdateFolderMutationParams
} from '@/types/highlight.interface'
import { getFolderNameError } from '@/utils/validateFolderName'

import HighlightDragOverlay from './HighlightDragOverlay'
import HighlightFolderItem from './HighlightFolderItem'

interface IProps {
	folders: IHighlightsFolder[]
	deleteHighlight: (
		params: IDeleteHighlightParams
	) => Promise<unknown> | unknown
	onOpenDeleteFolder: (folderId: number) => void
	updateFolder: ({
		highlightId,
		name,
		description
	}: IUpdateFolderMutationParams) => Promise<unknown> | unknown
	isUpdating: boolean
	isSelectMode: boolean
	selectedFolderIds: number[]
	onToggleFolder: (folderId: number) => void
	moveSegmentToFolder: (params: IMoveSegmentParams) => Promise<void>
	downloadFolder: (folder: IHighlightsFolder) => void
	downloadSegment: (params: {
		segment: ISegment
		folder: IHighlightsFolder
	}) => void
	shareFolder: (folderId: number) => void
	isSharedView?: boolean
	onScrollToSegment?: (segmentStart: number) => void
}

const HighlightList: FC<IProps> = ({
	folders,
	deleteHighlight,
	onOpenDeleteFolder,
	updateFolder,
	isUpdating,
	isSelectMode,
	selectedFolderIds,
	onToggleFolder,
	moveSegmentToFolder,
	downloadFolder,
	downloadSegment,
	shareFolder,
	isSharedView = false,
	onScrollToSegment
}) => {
	const [editedFolderId, setEditedFolderId] = useState<number | null>(null)
	const [editFolderTitle, setEditFolderTitle] = useState('')
	const [editFolderDesc, setEditFolderDesc] = useState('')
	const [folderNameError, setFolderNameError] = useState('')

	const [activeDragSegment, setActiveDragSegment] = useState<{
		segment: IMoveSegmentParams['segment']
		sourceFolderId: number
		segmentIndex: number
	} | null>(null)

	const editFolder = useDisclosure()

	const openEditFolder = (folderId: number) => {
		const folder = folders.find(folder => folder.id === folderId)

		if (!folder) return

		setEditedFolderId(folderId)
		setEditFolderTitle(folder.name)
		setEditFolderDesc(folder.description || '')
		setFolderNameError('')
		editFolder.onOpen()
	}

	const closeEditFolder = () => {
		editFolder.onClose()
		setEditedFolderId(null)
		setEditFolderTitle('')
		setEditFolderDesc('')
		setFolderNameError('')
	}

	const saveEditedFolder = async () => {
		if (!editedFolderId) return

		const trimmedTitle = editFolderTitle.trim()
		const trimmedDesc = editFolderDesc.trim()

		if (!trimmedTitle && !trimmedDesc) return

		const error = getFolderNameError(trimmedTitle, folders, editedFolderId)

		if (error) {
			setFolderNameError(error)
			return
		}

		const payload = {
			highlightId: editedFolderId,
			name: trimmedTitle || undefined,
			description: trimmedDesc || undefined
		}

		closeEditFolder()

		try {
			await updateFolder(payload)
		} catch (error) {
			console.log(error)
		}
	}

	const handleDragStart = (event: DragStartEvent) => {
		const activeData = event.active.data.current

		if (activeData?.type !== 'segment') return

		setActiveDragSegment({
			segment: activeData.segment,
			sourceFolderId: activeData.sourceFolderId,
			segmentIndex: activeData.segmentIndex
		})
	}

	const handleDragEnd = async (event: DragEndEvent) => {
		const { active, over } = event

		setActiveDragSegment(null)

		if (!over) return

		const activeData = active.data.current
		const overData = over.data.current

		if (activeData?.type !== 'segment') return
		if (overData?.type !== 'folder') return

		const sourceFolderId = activeData.sourceFolderId
		const targetFolderId = overData.folderId
		const segmentIndex = activeData.segmentIndex
		const segment = activeData.segment

		if (sourceFolderId === targetFolderId) return

		try {
			await moveSegmentToFolder({
				sourceFolderId,
				targetFolderId,
				segmentIndex,
				segment
			})
		} catch (error) {
			console.log(error)
		}
	}

	const handleDragCancel = () => {
		setActiveDragSegment(null)
	}

	return (
		<div className='relative flex flex-col h-full overflow-y-auto pb-16'>
			{isUpdating && (
				<div className='absolute inset-0 z-20 flex items-start justify-center bg-white/50 backdrop-blur-[1px]'>
					<Spinner className='w-12 h-12 top-[30%]' />
				</div>
			)}

			{!folders?.length && (
				<Typography
					variant='text-semibold'
					className='text-gray-600/40 m-auto text-center px-2'
				>
					Select the text and press Ctrl + C
				</Typography>
			)}

			<DndContext
				onDragStart={handleDragStart}
				onDragEnd={handleDragEnd}
				onDragCancel={handleDragCancel}
			>
				<div className={cn('flex flex-col mt-1', isUpdating && 'opacity-30')}>
					{folders.map(folder => (
						<HighlightFolderItem
							key={folder.id}
							folder={folder}
							onOpenDeleteFolder={onOpenDeleteFolder}
							deleteHighlight={deleteHighlight}
							onEditFolder={openEditFolder}
							isSelectMode={isSelectMode}
							onToggleFolder={onToggleFolder}
							isFolderSelected={selectedFolderIds.includes(folder.id)}
							downloadFolder={downloadFolder}
							downloadSegment={downloadSegment}
							shareFolder={shareFolder}
							isSharedView={isSharedView}
							onScrollToSegment={onScrollToSegment}
						/>
					))}
				</div>
				<DragOverlay dropAnimation={null}>
					{activeDragSegment ? (
						<HighlightDragOverlay segment={activeDragSegment.segment} />
					) : null}
				</DragOverlay>
			</DndContext>

			<DialogWindow
				handleClick={saveEditedFolder}
				open={editFolder.open}
				onClose={closeEditFolder}
				titleText='Edit folder'
				body={
					<>
						<div className='flex flex-col gap-2'>
							<Typography
								variant='text'
								className='!text-[16px] leading-[20px] text-black'
							>
								Folder name
							</Typography>
							<CustomInput
								value={editFolderTitle}
								onChange={e => {
									setEditFolderTitle(e.target.value)
									setFolderNameError('')
								}}
								onKeyDown={e => {
									if (
										e.key === 'Enter' &&
										!e.nativeEvent.isComposing &&
										!e.repeat
									) {
										e.preventDefault()
										saveEditedFolder()
									}
								}}
								placeholder='Add name folder'
								maxLength={50}
								errorMessage={folderNameError}
							/>
						</div>
						<div className='flex flex-col'>
							<Typography
								variant='text'
								className='!text-[16px] leading-[20px] text-black'
							>
								Note
							</Typography>
							<CustomTextarea
								value={editFolderDesc}
								onChange={e => setEditFolderDesc(e.target.value)}
								iconName=''
								fieldLabel=''
								placeholder='Add note'
								maxLength={100}
							/>
						</div>
					</>
				}
				bodyClassname='flex flex-col gap-4'
				noButtonText={
					<>
						<Cancel className='w-3 h-3' />
						<span>Cancel</span>
					</>
				}
				yesButtonText={
					<>
						<Check />
						<span>Save changes</span>
					</>
				}
				noButtonClassname='!text-[16px] hover:bg-neutral-200'
				noButtonClassnameText='flex items-center gap-3'
				yesButtonClassname='bg-neutral-900 hover:bg-neutral-700'
				yesButtonClassnameText='flex items-center gap-3'
			/>
		</div>
	)
}

export default HighlightList
